import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

import "../../FormStyles.css";
import "./CompetitionKnockout.css";
import useAxios from "../../useAxios";
import useSessionStorage from "../../useSessionStorage";

import Select from "react-select";
import PlayerDisplay from "../../Components/PlayerDisplay";

const CompetitionKnockout = forwardRef(({ competition, id }, ref) => {
  const [axios] = useAxios(true);
  const [token] = useSessionStorage("token", null);

  // Get user type from session storage
  const getUserType = () => {
    if (!token) return "user";

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.user_type || "user";
    } catch {
      return "user";
    }
  };

  const userType = getUserType();
  const isAdmin = userType === "admin";

  const [players, setPlayers] = useState(null);
  const [knockout, setKnockout] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerHandicap, setNewPlayerHandicap] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      await axios.get("players", setPlayers);
      await axios.get(
        "competitionknockout/" + (competition?.id ?? "new"),
        (k) => {
          setKnockout(k);
        }
      );
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setKnockout((prevcomp) => ({ ...prevcomp, [name]: value }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setKnockout((prevcomp) => ({ ...prevcomp, [name]: parseInt(value) }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setKnockout((prevcomp) => ({ ...prevcomp, [name]: checked }));
  };

  const handleSelectedPlayersChange = (options) => {
    setSelectedPlayers(options || []);
  };

  const addPlayers = () => {
    const gameTypeId = competition?.gameType?.id;
    const playersWithCorrectHandicap = selectedPlayers.map((player) => ({
      ...player,
      handicap: getHandicapForGameType(player, gameTypeId),
    }));

    setKnockout((prevcomp) => ({
      ...prevcomp,
      players: [...(prevcomp?.players || []), ...playersWithCorrectHandicap],
    }));
    setSelectedPlayers([]);
  };

  const removePlayer = (player) => {
    setKnockout((prevcomp) => ({
      ...prevcomp,
      players: (prevcomp?.players || []).filter((p) => p.id !== player.id),
    }));
  };

  const togglePlayerPaid = (playerId, isPaid) => {
    setKnockout((prevcomp) => ({
      ...prevcomp,
      players: (prevcomp?.players || []).map((player) =>
        player.id === playerId ? { ...player, paid: isPaid } : player
      ),
    }));
  };

  const movePlayerUp = (index) => {
    if (index === 0) return; // Can't move first player up

    setKnockout((prevcomp) => {
      const newPlayers = [...(prevcomp?.players || [])];
      // Swap with previous player
      [newPlayers[index - 1], newPlayers[index]] = [
        newPlayers[index],
        newPlayers[index - 1],
      ];
      return {
        ...prevcomp,
        players: newPlayers,
      };
    });

    // Call API to update order in backend
    updatePlayerOrder();
  };

  const movePlayerDown = (index) => {
    const playerCount = (knockout?.players || []).length;
    if (index === playerCount - 1) return; // Can't move last player down

    setKnockout((prevcomp) => {
      const newPlayers = [...(prevcomp?.players || [])];
      // Swap with next player
      [newPlayers[index], newPlayers[index + 1]] = [
        newPlayers[index + 1],
        newPlayers[index],
      ];
      return {
        ...prevcomp,
        players: newPlayers,
      };
    });

    // Call API to update order in backend
    updatePlayerOrder();
  };

  const updatePlayerOrder = () => {
    if (!knockout?.competition?.id || !knockout?.players) return;

    const playerIds = knockout.players.map((player) => player.id);
    axios.post(
      `competitionknockout/${knockout.competition.id}/reorder-players`,
      {
        playerIds: playerIds,
      }
    );
  };

  const createNewPlayer = () => {
    if (!newPlayerName.trim()) {
      alert("Player name is required");
      return;
    }

    const gameTypeId = competition?.gameType?.id;
    const newPlayer = {
      id: 0,
      name: newPlayerName,
      handicaps: gameTypeId ? [
        {
          gameTypeId: gameTypeId,
          value: newPlayerHandicap
        }
      ] : []
    };

    axios.post("player/new", newPlayer, (playerId) => {
      // Refresh the players list
      axios.get("players", (updatedPlayers) => {
        setPlayers(updatedPlayers);
        // Find the newly created player and add them directly to the competition
        const createdPlayer = updatedPlayers.find((p) => p.id === playerId);
        if (createdPlayer) {
          const gameTypeId = competition?.gameType?.id;
          setKnockout((prevcomp) => ({
            ...prevcomp,
            players: [
              ...(prevcomp?.players || []),
              {
                ...createdPlayer,
                handicap: getHandicapForGameType(createdPlayer, gameTypeId),
              },
            ],
          }));
        }
        // Clear the form
        setNewPlayerName("");
        setNewPlayerHandicap(0);
      });
    });
  };

  const handlePlayerUpdated = (updatedPlayer) => {
    const gameTypeId = competition?.gameType?.id;
    // Update the player in the knockout players list
    setKnockout((prevcomp) => ({
      ...prevcomp,
      players: (prevcomp?.players || []).map((p) =>
        p.id === updatedPlayer.id
          ? {
              ...p,
              name: updatedPlayer.name,
              handicap: getHandicapForGameType(updatedPlayer, gameTypeId),
              handicaps: updatedPlayer.handicaps
            }
          : p
      ),
    }));

    // Also refresh the full players list
    axios.get("players", setPlayers);
  };

  const handleOnSave = (competition, next) => {
    if (!knockout) return;
    knockout.competition = competition;
    axios.post("competitionknockout/" + competition.id, knockout, next);
  };

  // Expose the function to the parent component through the ref
  useImperativeHandle(ref, () => ({
    onSave: handleOnSave,
  }));

  if (knockout === null || players === null) {
    return <h1>Loading</h1>;
  }

  // Helper function to get handicap for a specific game type
  const getHandicapForGameType = (player, gameTypeId) => {
    if (!gameTypeId) {
      return player?.handicap ?? 0;
    }
    // Note: Backend uses 'value' property, not 'handicap'
    const handicapEntry = (player?.handicaps || []).find((h) => h.gameTypeId === gameTypeId);
    return handicapEntry?.value ?? 0;
  };

  const availablePlayers = (players || []).filter((p) => {
    // Filter out players already in the competition
    return !(knockout?.players || []).some((kp) => kp.id === p.id);
  });

  // Check if competition has started (status != 0 means not pending)
  // Also check if editing existing competition (id !== "new")
  const isCompetitionStarted = id !== "new" && knockout?.competition?.status !== 0;

  return (
    <div className="edit-knockout-grid">
      <div className="col1">
        <div className="form-group">
          <label htmlFor="raceTo">Race To:</label>
          <input
            name="raceTo"
            id="raceTo"
            type="number"
            value={knockout.raceTo}
            onChange={handleChange}
            min={0}
            max={100}
            className="form-control"
            disabled={isCompetitionStarted}
          />
        </div>
        <div className="form-group">
          <label htmlFor="matchTimeLimit">Match Time Limit:</label>
          <input
            name="matchTimeLimit"
            id="matchTimeLimit"
            type="number"
            value={knockout.matchTimeLimit}
            onChange={handleNumberChange}
            min={0}
            max={300}
            className="form-control"
            disabled={isCompetitionStarted}
          />
        </div>
        <div className="form-group">
          <label htmlFor="handicapped">Handicapped:</label>
          <input
            name="handicapped"
            id="handicapped"
            type="checkbox"
            checked={knockout.handicapped}
            onChange={handleCheckboxChange}
            className="form-control"
            style={{ width: "auto" }}
            disabled={isCompetitionStarted}
          />
        </div>
        <div className="form-group">
          <label>Available Players:</label>
          <Select
            isMulti
            value={selectedPlayers}
            onChange={handleSelectedPlayersChange}
            options={availablePlayers}
            placeholder="Select players to add..."
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => `${option.name} (${getHandicapForGameType(option, competition?.gameType?.id)})`}
            isSearchable={false}
            closeMenuOnSelect={false}
            className="players-select"
            styles={{
              container: (base) => ({
                ...base,
                maxWidth: "50vw",
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            blurInputOnSelect={false}
            isDisabled={isCompetitionStarted}
          />
          <button
            type="button"
            onClick={addPlayers}
            disabled={selectedPlayers.length === 0 || isCompetitionStarted}
            className="submit-button"
            style={{ marginTop: "10px", maxWidth: 220 }}
          >
            Add Selected Players
          </button>
        </div>
        <div className="form-group">
          <label>Create New Player:</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: "1", minWidth: "200px" }}>
              <label htmlFor="newPlayerName" style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
                Name:
              </label>
              <input
                id="newPlayerName"
                type="text"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                placeholder="Player name"
                className="form-control"
                disabled={isCompetitionStarted}
              />
            </div>
            <div style={{ width: "100px" }}>
              <label htmlFor="newPlayerHandicap" style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
                Handicap:
              </label>
              <input
                id="newPlayerHandicap"
                type="number"
                value={newPlayerHandicap}
                onChange={(e) => setNewPlayerHandicap(parseInt(e.target.value) || 0)}
                placeholder="0"
                min={0}
                max={100}
                className="form-control"
                disabled={isCompetitionStarted}
              />
            </div>
            <button
              type="button"
              onClick={createNewPlayer}
              disabled={!newPlayerName.trim() || isCompetitionStarted}
              className="submit-button"
              style={{ marginBottom: 0, maxWidth: 150 }}
            >
              Create & Add
            </button>
          </div>
        </div>
        <div className="form-group">
          <label>Entries:</label>
          <table
            className="entries-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "8px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    borderBottom: "1px solid #ddd",
                    width: "50px",
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  Player
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    borderBottom: "1px solid #ddd",
                    width: "100px",
                  }}
                >
                  Handicap
                </th>
                {isAdmin && (
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      borderBottom: "1px solid #ddd",
                      width: "80px",
                    }}
                  >
                    Paid
                  </th>
                )}
                <th
                  style={{
                    padding: "8px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    width: "80px",
                  }}
                >
                  Order
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    width: "60px",
                  }}
                >
                  Remove
                </th>
              </tr>
            </thead>
            <tbody>
              {(knockout.players || []).map((player, index) => (
                <tr key={player.id}>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #eee",
                      textAlign: "left",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #eee",
                      textAlign: "left",
                    }}
                  >
                    <PlayerDisplay
                      player={player}
                      onPlayerUpdated={handlePlayerUpdated}
                      gameTypeId={competition?.gameType?.id}
                    />
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #eee",
                      textAlign: "left",
                    }}
                  >
                    ({getHandicapForGameType(player, competition?.gameType?.id)})
                  </td>
                  {isAdmin && (
                    <td
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #eee",
                        textAlign: "center",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={player.paid || false}
                        onChange={(e) =>
                          togglePlayerPaid(player.id, e.target.checked)
                        }
                        disabled={isCompetitionStarted}
                      />
                    </td>
                  )}
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #eee",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => movePlayerUp(index)}
                        disabled={index === 0 || isCompetitionStarted}
                        style={{
                          padding: "2px 6px",
                          minWidth: 0,
                          fontSize: "12px",
                          backgroundColor: index === 0 || isCompetitionStarted ? "#ccc" : "#007bff",
                          color: "white",
                          border: "none",
                          borderRadius: "2px",
                          cursor: index === 0 || isCompetitionStarted ? "not-allowed" : "pointer",
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => movePlayerDown(index)}
                        disabled={index === (knockout.players || []).length - 1 || isCompetitionStarted}
                        style={{
                          padding: "2px 6px",
                          minWidth: 0,
                          fontSize: "12px",
                          backgroundColor:
                            index === (knockout.players || []).length - 1 || isCompetitionStarted
                              ? "#ccc"
                              : "#007bff",
                          color: "white",
                          border: "none",
                          borderRadius: "2px",
                          cursor:
                            index === (knockout.players || []).length - 1 || isCompetitionStarted
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #eee",
                      textAlign: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => removePlayer(player)}
                      className="cancel-button"
                      style={{ padding: "2px 8px", minWidth: 0 }}
                      disabled={isCompetitionStarted}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default CompetitionKnockout;
